import { Visibility } from "../types/subscription";
import httpService from "./httpService";

// Baskets API service
export interface BasketStock {
  id: string;
  basketId: string;
  conid: number;
  weight: number;
  positionType: "LONG" | "SHORT";
  createdAt: string;
  updatedAt: string;
  // Enriched fields from instrument service
  ticker?: string;
  name?: string;
  isin?: string;
  exchange?: string;
  primaryExchange?: string;
  secType?: string;
  price?: number;
  currency?: string;
  timestamp?: string;
  source?: string;
  // Option related fields
  optionAllowed?: boolean;
  optionExchange?: string;
  optionType?: string;
}

export interface Basket {
  id: string;
  name: string;
  description: string | null;
  providerId: string;
  status: "ACTIVE" | "INACTIVE";
  visibility: Visibility;
  isSubscribed?: boolean;
  createdAt: string;
  updatedAt: string;
  basketStocks?: BasketStock[];
}

export interface BasketsResponse {
  success: boolean;
  message: string;
  baskets: Basket[];
}

class BasketsService {
  async getProviderBaskets(providerId: string, enriched: boolean = false): Promise<Basket[]> {
    try {
      const url = enriched ? `providers/${providerId}/baskets?enriched=true` : `providers/${providerId}/baskets`;

      const response = await httpService.get<BasketsResponse>(url);
      return response.data.baskets;
    } catch (error) {
      console.error("Baskets fetch error:", error);
      throw error;
    }
  }

  async createBasket(
    providerId: string,
    basketData: {
      name: string;
      description?: string;
      stocks?: Array<{
        conid: number;
        weight: number;
        positionType: "LONG" | "SHORT";
      }>;
    }
  ): Promise<Basket> {
    try {
      const response = await httpService.post<{ basket: Basket }>(`/v2/providers/${providerId}/baskets`, basketData);
      return response.data.basket;
    } catch (error) {
      console.error("Create basket error:", error);
      throw error;
    }
  }



  async getBasketById(
    providerId: string,
    basketId: string,
    accessScope: 'provider' | 'investor' = 'provider'
  ): Promise<Basket> {
    try {
      const suffix = accessScope === 'investor' ? '/investors' : '/providers';
      const response = await httpService.get<{ basket: Basket }>(
        `/v2/providers/${providerId}/baskets/${basketId}${suffix}`
      );
      return response.data.basket;
    } catch (error) {
      console.error("Get basket by ID error:", error);
      throw error;
    }
  }

  async updateBasket(
    providerId: string,
    basketId: string,
    basketData: {
      name?: string;
      description?: string;
      visibility?: Visibility;
      status?: "ACTIVE" | "INACTIVE";
      stocks?: Array<{
        conid: number;
        weight: number;
        positionType: "LONG" | "SHORT";
      }>;
    }
  ): Promise<Basket> {
    try {
      const response = await httpService.put<{ basket: Basket }>(
        `/v2/providers/${providerId}/baskets/${basketId}`,
        basketData
      );
      return response.data.basket;
    } catch (error) {
      console.error("Update basket error:", error);
      throw error;
    }
  }

  async updateBasketVisibility(
    providerId: string,
    basketId: string,
    visibility: Visibility
  ): Promise<Basket> {
    try {
      // Use 'provider' scope since visibility updates are provider-only operations
      const basket = await this.getBasketById(providerId, basketId, 'provider');
      if (!basket.basketStocks) {
        throw new Error("Basket stocks missing; cannot update visibility without full payload.");
      }
      const stocks = basket.basketStocks.map(stock => {
        if (stock.conid == null || stock.weight == null || !stock.positionType) {
          throw new Error("Basket stock data incomplete; cannot update visibility.");
        }
        return {
          conid: stock.conid,
          weight: stock.weight,
          positionType: stock.positionType,
        };
      });
      return this.updateBasket(providerId, basketId, {
        name: basket.name,
        description: basket.description ?? undefined,
        visibility,
        stocks,
      });
    } catch (error) {
      console.error("Update basket visibility error:", error);
      throw error;
    }
  }

  async deleteBasket(providerId: string, basketId: string): Promise<void> {
    try {
      await httpService.delete(`providers/${providerId}/baskets/${basketId}`);
    } catch (error) {
      console.error("Delete basket error:", error);
      throw error;
    }
  }
}

export const basketsService = new BasketsService();
